/**
 * @file api.ts
 * @brief TanStack Query hooks for the Super Admin → Users feature.
 *
 * @details
 * - Centralizes every query/mutation against `/api/admin/users/*`.
 * - All inputs validated client-side before transmission; all responses
 *   parsed against Zod schemas before being handed to React.
 * - Mutations invalidate the list query so the table reflects updates
 *   without manual refetches.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client (apiGet/Post/Put/Delete)
 * - shared/api/endpoints (URL registry)
 * - features/users/schemas
 *
 * @references
 * - BACKEND/src/features/admin/routes.js
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  AdminUserSchema,
  AdminUsersListResponseSchema,
  LoginHistoryResponseSchema,
  type AdminUser,
  type AdminUsersListParams,
  type AdminUsersListResponse,
  type CreateUserInput,
  type LoginEvent,
  type UpdateUserInput,
} from "./schemas";

// ----------------------------------------------------------------------
// Query keys
// Centralised so cache invalidation is consistent.
// ----------------------------------------------------------------------
export const usersKeys = {
  all: ["admin", "users"] as const,
  list: (params: AdminUsersListParams) =>
    ["admin", "users", "list", params] as const,
  detail: (id: string) => ["admin", "users", "detail", id] as const,
  loginHistory: (id: string) =>
    ["admin", "users", "login-history", id] as const,
};

// ----------------------------------------------------------------------
// useUsersList — paginated, filterable list
// ----------------------------------------------------------------------
function buildListSearchParams(params: AdminUsersListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set("page", String(params.page));
  if (params.limit) usp.set("limit", String(params.limit));
  if (params.role) usp.set("role", params.role);
  if (params.status) usp.set("status", params.status);
  if (params.company_id) usp.set("company_id", params.company_id);
  if (params.search) usp.set("search", params.search);
  if (params.native_language) usp.set("native_language", params.native_language);
  if (params.billing) usp.set("billing", params.billing);
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function fetchUsersPage(
  params: AdminUsersListParams,
): Promise<AdminUsersListResponse> {
  const url = `${adminEndpoints.users}${buildListSearchParams(params)}`;
  const raw = await apiGet<unknown>(url);
  return AdminUsersListResponseSchema.parse(raw);
}

export function useUsersList(
  params: AdminUsersListParams,
  options?: Partial<UseQueryOptions<AdminUsersListResponse>>,
) {
  return useQuery<AdminUsersListResponse>({
    queryKey: usersKeys.list(params),
    queryFn: () => fetchUsersPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

/** One company in the grouped list (`?group=company`, DES 01-10-2026). */
export const UsersCompanyGroupSchema = z.object({
  company_id: z.string().nullable(),
  company_name: z.string().nullable(),
  count: z.number(),
});
export type UsersCompanyGroup = z.infer<typeof UsersCompanyGroupSchema>;

/** The group key for a company; `none` for users with no company. */
export const usersCompanyGroupKey = (companyId: string | null) => companyId ?? "none";

/** The list filters without paging (shared by the grouped calls). */
function filterQuery(params: AdminUsersListParams): URLSearchParams {
  return new URLSearchParams(
    buildListSearchParams({ ...params, page: undefined, limit: undefined }).replace(/^\?/, ""),
  );
}

/**
 * Every company with users under the current filters, with counts (DES
 * 01-10-2026: "haan superadmin pages pe bhi yehi kar do"), as on company
 * Invoices. One server GROUP BY instead of loading every user to group them.
 */
export function useUsersGroupSummary(params: AdminUsersListParams, enabled: boolean) {
  return useQuery({
    queryKey: [...usersKeys.all, "groups", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "company");
      const raw = await apiGet<{ groups?: unknown }>(`${adminEndpoints.users}?${usp}`);
      return z.array(UsersCompanyGroupSchema).parse(raw.groups ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const USER_GROUP_BATCH = 25;

/**
 * The users of each OPEN company group, `batches` x USER_GROUP_BATCH, under
 * the same filters (`?company=`). The route does not cap `limit`, so one
 * request asks for every loaded batch.
 */
export function useUsersGroupRows(
  params: AdminUsersListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [...usersKeys.all, "group", key, batches, filterQuery(params).toString()],
      placeholderData: (prev: AdminUsersListResponse | undefined) => prev,
      queryFn: async (): Promise<AdminUsersListResponse> => {
        const usp = filterQuery(params);
        usp.set("company", key);
        usp.set("page", "1");
        usp.set("limit", String(batches * USER_GROUP_BATCH));
        const raw = await apiGet<unknown>(`${adminEndpoints.users}?${usp}`);
        return AdminUsersListResponseSchema.parse(raw);
      },
    })),
  });
}

// ----------------------------------------------------------------------
// useUser — single user by id
// ----------------------------------------------------------------------
export function useUser(id: string | null, enabled = true) {
  return useQuery<AdminUser>({
    queryKey: usersKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.userById(id!));
      return AdminUserSchema.parse(raw);
    },
  });
}

// ----------------------------------------------------------------------
// useUserLoginHistory — per-user login-location audit trail (recent first).
// ----------------------------------------------------------------------
export function useUserLoginHistory(id: string | null, enabled = true) {
  return useQuery<LoginEvent[]>({
    queryKey: usersKeys.loginHistory(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        adminEndpoints.userLoginHistory(id!),
      );
      return LoginHistoryResponseSchema.parse(raw).history;
    },
  });
}

// ----------------------------------------------------------------------
// useUserOptions — lightweight list hook for pickers / autocomplete.
// Returns up to 25 users matching the search term. Mirrors the
// `useCompanyOptions` shape so picker components can stay symmetric.
// DES Added: (26-05-2026)
// ----------------------------------------------------------------------
export function useUserOptions(search: string, enabled = true) {
  const trimmed = search.trim();
  return useQuery<AdminUsersListResponse>({
    queryKey: ["admin", "users", "options", trimmed] as const,
    enabled,
    queryFn: async () => {
      const usp = new URLSearchParams();
      usp.set("limit", "25");
      if (trimmed) usp.set("search", trimmed);
      const url = `${adminEndpoints.users}?${usp.toString()}`;
      const raw = await apiGet<unknown>(url);
      return AdminUsersListResponseSchema.parse(raw);
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

// ----------------------------------------------------------------------
// Mutations
// ----------------------------------------------------------------------
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<AdminUser, Error, CreateUserInput>({
    mutationFn: async (input) => {
      const raw = await apiPost<unknown>(adminEndpoints.users, input);
      return AdminUserSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<
    AdminUser,
    Error,
    { id: string; input: UpdateUserInput }
  >({
    mutationFn: async ({ id, input }) => {
      const raw = await apiPut<unknown>(adminEndpoints.userById(id), input);
      return AdminUserSchema.parse(raw);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
      void qc.invalidateQueries({ queryKey: usersKeys.detail(vars.id) });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) => apiDelete<unknown>(adminEndpoints.userById(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) => apiPost<unknown>(adminEndpoints.userSuspend(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) =>
      apiPost<unknown>(adminEndpoints.userReactivate(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useChangeUserPassword() {
  return useMutation<
    unknown,
    Error,
    { id: string; newPassword: string }
  >({
    mutationFn: async ({ id, newPassword }) =>
      apiPost<unknown>(adminEndpoints.userChangePassword(id), { newPassword }),
  });
}

export function useResendWelcomeEmail() {
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) =>
      apiPost<unknown>(adminEndpoints.userResendWelcome(id)),
  });
}

/**
 * GDPR request that reached Verblizr directly (05-10-2026): the server emails
 * the person's data to the ACCOUNT address as a 7-day link. VERBLIZR staff
 * never get the zip (B2/W4, DES 22-10-2026). 502 EXPORT_EMAIL_FAILED = the
 * email did not go, and there is no link. Logged in the activity log.
 */
export const UserExportSentSchema = z.object({
  sentTo: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
});
export type UserExportSent = z.infer<typeof UserExportSentSchema>;

export function useSendUserDataExport() {
  return useMutation<UserExportSent, Error, string>({
    mutationFn: async (id) =>
      UserExportSentSchema.parse(await apiPost<unknown>(adminEndpoints.userExport(id))),
  });
}
